import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { MILLIY_LADDER } from 'modules/resultView/categoryResult';
import { categoryTheme } from './categoryThemes';
import type { SubjectLevel } from './subjectLevel';

interface SubjectLevelPanelProps {
  /** The subject's name in the learner's language. */
  subject: string;
  progress: SubjectLevel;
  className?: string;
}

/** Highest grade first, so the ladder reads top-down like the board. */
const STEPS = [...MILLIY_LADDER].reverse();

/**
 * Where the learner stands in one Milliy subject: the grade of their best finished attempt on the ladder, their
 * own goal when they set one, and the topics they missed most when the questions name topics. Nothing here is
 * invented: a missing goal or topic leaves its line out.
 */
export function SubjectLevelPanel({ subject, progress, className }: SubjectLevelPanelProps) {
  const { t } = useTranslation();
  const { accent } = categoryTheme('milliy-sertifikat');
  const titleId = React.useId();
  const { level, goal, weakTopics } = progress;
  const reachedAt = level ? STEPS.findIndex((s) => s.label === level) : STEPS.length;

  const standing =
    level !== null
      ? t('portal_subject_level_now', { level })
      : progress.ball !== null
        ? t('portal_subject_level_none', { ball: progress.ball })
        : null;

  return (
    <aside aria-labelledby={titleId} className={cn('rounded-2xl border border-border bg-card p-5 md:p-[22px]', className)}>
      <h2 id={titleId} className="font-display text-[17px] font-extrabold tracking-[-0.03em] text-foreground">
        {t('portal_subject_level_title', { subject })}
      </h2>
      {(standing || goal) && (
        <p className="mb-3.5 mt-1 text-[12.5px] text-muted-foreground">{[standing, goal ? t('portal_subject_goal', { level: goal }) : null].filter(Boolean).join(' ')}</p>
      )}
      <ol aria-label={t('portal_subject_ladder')} className="flex flex-col gap-1.5">
        {STEPS.map((step, i) => {
          const here = step.label === level;
          const target = step.label === goal && !here;
          const reached = i > reachedAt;
          return (
            <li
              key={step.label}
              aria-current={here ? 'step' : undefined}
              style={
                here
                  ? { background: accent, borderColor: accent, color: '#fff' }
                  : target
                    ? { borderColor: accent, color: accent }
                    : reached
                      ? { background: `color-mix(in srgb, ${accent} 13%, hsl(var(--card)))`, borderColor: 'transparent', color: accent }
                      : undefined
              }
              className={cn(
                'flex h-10 items-center gap-3 rounded-xl border-2 px-3 font-display text-[15px] font-extrabold',
                target && 'border-dashed bg-card',
                !here && !target && !reached && 'border-transparent bg-muted text-muted-foreground',
              )}
            >
              <span className="w-8">{step.label}</span>
              {here && <span className="text-xs font-bold">{t('portal_subject_you_here')}</span>}
              {target && <span className="text-xs font-bold">{t('portal_subject_goal_step')}</span>}
            </li>
          );
        })}
      </ol>
      {weakTopics.length > 0 && (
        <p className="mt-3.5 text-[12.5px] leading-relaxed text-muted-foreground">{t('portal_subject_weak', { topics: weakTopics.join(', ') })}</p>
      )}
      {progress.ball !== null && <p className="mt-3.5 text-[12px] leading-relaxed text-muted-foreground">{t('portal_subject_ball_note')}</p>}
    </aside>
  );
}

export default SubjectLevelPanel;
